<template>
  <section :class="[$style.collection, 'unspaced']">
    <h2>From the Collection</h2>
    <span :class="screenreaderOnly">Random Artwork From The Guggenheim Collection</span>
    <router-link
      v-if="artwork && artwork.featuredImage"
      :class="$style.focus"
      :to="`/artwork/${artwork.slug}`"
      :ga4-event="
        JSON.stringify({
          click_type: 'from_the_collection',
          component: 'FromTheCollection',
          content_group: 'homepage',
          gtm_tag: 'interaction',
          link_text: `${loaded.artist} ${loaded.title}${loaded.dates}`,
        })
      "
    >
      <picture>
        <img
          :alt="artwork.featuredImage.node.altText"
          :src="artwork.featuredImage.node.sourceUrl"
          :srcset="artwork.imageSrcset"
          aria-live="polite"
          loading="lazy"
          sizes="(min-width: 960px) 1280px, 100vw"
          @load="load()"
        />
      </picture>
    </router-link>
    <h3 v-html="loaded.artist"></h3>
    <p class="heading-5 space-8-10-above">
      <span :class="$style.italic" v-html="loaded.title"></span>
      <span v-html="loaded.dates"></span>
    </p>
    <div :class="$style.buttons">
      <button
        class="button-secondary"
        type="button"
        :disabled="disabled"
        @click.prevent="refresh()"
        @keyup.enter="refresh()"
      >
        Load Another Artwork
        <IconRefresh :class="{ [$style.loading]: disabled }"></IconRefresh>
      </button>
      <router-link
        :to="{
          name: 'CollectionOnline',
        }"
        class="button-secondary"
        :ga4-event="
          JSON.stringify({
            click_type: 'from_the_collection',
            component: 'FromTheCollection',
            content_group: 'homepage',
            gtm_tag: 'interaction',
            link_text: 'Browse the Collection',
          })
        "
      >
        Browse the Collection
      </router-link>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Artwork } from '@/types/common.interface';
import { BootStrap } from '@/types/global.interface';
import { fromTheCollectionQuery } from '@/queries/FromTheCollectionQuery';
import gql from 'graphql-tag';
import IconRefresh from '@/assets/svgs/IconRefresh.svg?component';
import { inject, ref } from 'vue';
import { screenreaderOnly } from '@/styles/a11y.module.scss';
import { storeToRefs } from 'pinia';
import { useAnalytics, useHandleError } from '@/composables/Common.js';
import { useMainStore } from '@/stores/mainStore.js';
import { useQuery } from 'villus';

const bootstrap = inject<BootStrap>('bootstrap');

const { ga4Event } = useAnalytics();
const { handleError } = useHandleError();

const store = useMainStore();
const { setValue } = store;
const { collectionArtworks, useBootstrap } = storeToRefs(store);

const artwork = ref<Artwork>({});
const disabled = ref(false);
const loaded = ref<{
  artist: string;
  dates: string;
  slug: string;
  title: string;
}>({});
const shouldSkip = ref(true);
const waiting = ref(false);

const { data, error, execute } = useQuery({
  onData: (data) => {
    if (data && Object.keys(data).length) {
      setArtworks(data.artworks.nodes);

      if (waiting.value) {
        artwork.value = getArtwork();
      }
    }

    if (!data || Object.keys(data).length === 0) {
      handleError('FromTheCollection', error);
    }
  },
  query: gql`
    ${fromTheCollectionQuery}
  `,
  skip: shouldSkip,
  variables: { filter: 'allow_footer' },
});

if (useBootstrap.value) {
  setArtworks(bootstrap.initial.main.posts.artworks);
  artwork.value = getArtwork();
}

if (!useBootstrap.value) {
  disabled.value = collectionArtworks.value.length === 0;
  waiting.value = collectionArtworks.value.length === 0;

  if (collectionArtworks.value.length > 0) {
    artwork.value = getArtwork();
  }

  if (collectionArtworks.value.length === 0 || collectionArtworks.value.length === 2) {
    shouldSkip.value = false;
    execute({ cachePolicy: 'network-only' });
  }
}

function getArtwork(): Artwork {
  const artwork = collectionArtworks.value.shift();

  setValue({ type: 'collectionArtworks', value: collectionArtworks.value });

  return artwork;
}

function load(): void {
  disabled.value = false;
  loaded.value = {
    artist: artwork.value.artists.nodes[0].name,
    dates: artwork.value.dates,
    slug: artwork.value.slug,
    title: `${artwork.value.title}, `,
  };
}

function refresh(): void {
  disabled.value = true;
  waiting.value = collectionArtworks.value.length === 0;

  if (collectionArtworks.value.length > 0) {
    artwork.value = getArtwork();

    if (collectionArtworks.value.length === 2) {
      shouldSkip.value = false;
      execute({ cachePolicy: 'network-only' });
    }
  }

  ga4Event({
    click_type: 'from_the_collection',
    component: 'FromTheCollection',
    content_group: 'homepage',
    gtm_tag: 'interaction',
    link_text: 'Load Another Artwork',
  });
}

function setArtworks(artworks: Array<Artwork>): void {
  collectionArtworks.value.push(...artworks);
  setValue({ type: 'collectionArtworks', value: collectionArtworks.value });
}
</script>

<style lang="scss" module>
@keyframes spin {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

.buttons {
  width: fit-content;

  a,
  button {
    display: block;
    margin: 2rem auto 0 0;
    width: fit-content;
  }
}

.collection {
  picture {
    img {
      @include space-24-32-above;
    }
  }

  &:global(.unspaced) {
    > h3:first-of-type {
      @include space-12-16-above;
    }
  }

  &::before {
    @include line;

    & {
      margin-top: -2rem;
    }
  }
}

.focus {
  &:focus {
    img {
      @include focus-outline;
    }
  }
}

.italic {
  font-style: italic;
}

.loading {
  animation: spin 0.6s linear infinite;
}

@media (min-width: $break-point) {
  .buttons {
    display: grid;
    column-gap: 2rem;
    grid-template-columns: 1fr 1fr;

    a,
    button {
      display: inline-block;
      margin: 2rem 0 0 0;
    }
  }

  .collection {
    grid-column: span 12;

    picture {
      @include aspect-container;

      img {
        @include aspect-image;
        @include space-24-32-above;
      }
    }
  }
}
</style>
